import React, { useState } from "react";
import { Link } from "gatsby";
import { Container } from "react-bootstrap";
import styled from "styled-components";

import { ACCENT, INK, BODY, LINE, scanNav } from "./tokens";
import { SCANNING_PHONE, SCANNING_PHONE_HREF } from "./contact";

// The scanning section's own header. It replaces the site header on every
// /document-scanning/ page (see components/Layout/Layout.js), so a visitor who
// lands here from search sees a document scanning business and nothing about
// mail: no mail nav, no Sign up button.
const Bar = styled.header`
  > .container { max-width: 1280px; }
  background: #ffffff;
  border-bottom: 1px solid ${LINE};
  position: relative;
  z-index: 50;
`;

const Inner = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 0;
`;

const ContactBar = styled.div`
  background: #f8fafc;
  border-bottom: 1px solid ${LINE};
  .container { max-width: 1280px; display: flex; justify-content: flex-end; align-items: center; flex-wrap: wrap; gap: 6px 12px; padding-top: 8px; padding-bottom: 8px; }
  span { color: ${BODY}; font-size: 12px; }
  a { color: ${ACCENT}; font-weight: 600; font-size: 14px; white-space: nowrap; text-decoration: none; }
  a:hover { text-decoration: underline; }
  a:focus-visible { outline: 2px solid ${ACCENT}; outline-offset: 3px; }
`;

const Lockup = styled(Link)`
  display: flex;
  align-items: baseline;
  gap: 11px;
  text-decoration: none;

  &:hover {
    text-decoration: none;
  }

  .dot {
    width: 11px;
    height: 11px;
    border-radius: 50%;
    background: ${ACCENT};
    align-self: center;
  }

  .mark {
    background: transparent;
    padding: 0;
    font-weight: 700;
    font-size: 20px;
    letter-spacing: -0.4px;
    color: ${INK};
  }

  .service {
    font-size: 16px;
    font-weight: 600;
    color: ${ACCENT};
    border-left: 1px solid ${LINE};
    padding-left: 11px;
  }

  @media (max-width: 400px) { gap: 7px; .mark { font-size: 18px; } .service { font-size: 13px; } }

  @media (max-width: 400px) {
    .service {
      border-left: 0;
      padding-left: 0;
      flex-basis: 100%;
    }
  }
`;

const Nav = styled.nav`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 14px;

  a {
    font-size: 13px;
    color: ${BODY};
    text-decoration: none;
    white-space: nowrap;

    &:hover {
      color: ${ACCENT};
      text-decoration: none;
    }

    &.active {
      color: ${INK};
      font-weight: 600;
    }
  }
  a[data-cta="true"] { color: #fff; }
  a:focus-visible { outline: 3px solid ${ACCENT}; outline-offset: 4px; }
  @media (max-width: 1199px) { align-items: stretch; flex-direction: column; gap: 0; a { padding: 12px 0; } a[data-cta="true"] { padding: 12px 20px; text-align: center; margin-top: 10px; } }
`;

const Quote = styled(Link)`
  background: ${ACCENT};
  color: #ffffff;
  font-size: 14px;
  font-weight: 600;
  padding: 10px 20px;
  border-radius: 6px;
  text-decoration: none;
  white-space: nowrap;

  &:hover {
    background: #3730a3;
    color: #ffffff;
    text-decoration: none;
  }
`;

const Toggle = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: 1px solid ${LINE};
  border-radius: 6px;
  padding: 9px 14px;
  font-size: 14px;
  font-weight: 600;
  color: ${INK};
  cursor: pointer;

  @media (min-width: 1200px) {
    display: none;
  }
`;

// data-open rather than a prop: styled-components v4 forwards unknown props to
// the DOM, and React warns about them.
const Collapse = styled.div`
  width: 100%;
  display: none;
  padding-bottom: 12px;

  &[data-open="true"] {
    display: block;
  }

  @media (min-width: 1200px) {
    display: contents;
    width: auto;
    padding-bottom: 0;

    &[data-open="true"] {
      display: contents;
    }
  }
`;

// Gatsby hands us the path with or without a trailing slash depending on where
// it came from; normalise both sides before comparing.
const slash = (p) => (p && p.endsWith("/") ? p : `${p || ""}/`);

const ScanHeader = ({ current }) => {
  const [open, setOpen] = useState(false);
  const here = slash(current);

  return (
    <Bar>
      <ContactBar><Container><span>Document scanning enquiries</span><a href={SCANNING_PHONE_HREF} aria-label={`Call HotSnail Document Scanning on ${SCANNING_PHONE}`}>{SCANNING_PHONE}</a></Container></ContactBar>
      <Container>
        <Inner>
          <Lockup to="/document-scanning/">
            <span className="dot" />
            <span className="mark">hotsnail</span>
            <span className="service">Document Scanning</span>
          </Lockup>

          <Toggle
            type="button"
            aria-controls="scanning-navigation"
            aria-expanded={open}
            onClick={() => setOpen(!open)}
          >
            Menu
          </Toggle>

          <Collapse data-open={open ? "true" : "false"}>
            <Nav id="scanning-navigation" aria-label="Document scanning">
              {scanNav.map(({ to, label }) => (
                <Link
                  key={to}
                  to={to}
                  className={here === slash(to) ? "active" : ""}
                  aria-current={here === slash(to) ? "page" : undefined}
                  onClick={() => setOpen(false)}
                >
                  {label}
                </Link>
              ))}
              <Quote data-cta="true" to="/document-scanning/#quote" onClick={() => setOpen(false)}>Get a quote</Quote>
            </Nav>
          </Collapse>
        </Inner>
      </Container>
    </Bar>
  );
};

export default ScanHeader;
